import { roomLib, ticketLib } from "@agentcall/core/lib"
import { formatPhone } from "@agentcall/core/lib/tenant_lib"
import { ticketType } from "@agentcall/core/type"
import { format } from "date-fns"
import { useAtomValue } from "jotai"
import { Suspense, useEffect, useState } from "react"
import { AlertTriangle } from "react-feather"
import { useLocation } from "react-router"
import { useParams } from "react-router-dom"
import { toast } from "sonner"
import { ownerApi } from "@/api/client/owner"
import { propertyApi } from "@/api/client/property"
import { tenantApi } from "@/api/client/tenant"
import type { TicketModels } from "@/api/client/ticket"
import { ticketApi } from "@/api/client/ticket"
import {
  TicketGetWithOcAssignee,
  TicketGetWithOwner,
  TicketGetWithProperty,
  TicketGetWithRoom,
  TicketGetWithRoomPlans,
  TicketGetWithTenant,
  TicketGetWithTicketComments,
  TicketGetWithTicketCommentsContactFromAdmin,
  TicketGetWithTicketCommentsContactFromOwner,
  TicketGetWithTicketCommentsContactFromTenant,
  TicketGetWithTicketCommentsContactToAdmin,
  TicketGetWithTicketCommentsContactToOwner,
  TicketGetWithTicketCommentsContactToTenant,
  type TicketMessageList200TicketMessagesItem,
} from "@/api/generated/models"
import {
  TicketProgressAddModal,
  type TicketProgressTransferInitialValues,
} from "@/components/ticket"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { ReactErrorBoundary } from "@/components/ui/error-boundary"
import { Label } from "@/components/ui/label"
import { SectionLoading } from "@/components/ui/loading"
import { usePermission } from "@/features/auth/hooks/usePermission"
import { openInNewTab } from "@/lib/navigation"
import { cn } from "@/lib/utils"
import { sessionUserAtom } from "@/store/session"
import {
  ContactInfo,
  NoteDetailModal,
  NoteEditModal,
  NoteSection,
  PropertyOwnerInfo,
  SupportHistory,
  SupportHistoryEditModal,
  TicketInfoTab,
} from "."

type ActiveTabType = "progress" | "info"
type HistoryTabType = "supportHistory" | "updateHistory"
type TicketComment = TicketModels.TicketGet200TicketTicketCommentsItem

interface TicketDetailContentProps {
  activeTab: ActiveTabType
}

export function TicketDetailContent({ activeTab }: TicketDetailContentProps) {
  const location = useLocation()
  const { ticketId } = useParams<{ ticketId: string }>()
  const [historyTab, setHistoryTab] = useState<HistoryTabType>("supportHistory")
  const [opensAddSupportHistoryModal, setOpensAddSupportHistoryModal] = useState(false)
  const [progressTransferInitialValues, setProgressTransferInitialValues] =
    useState<TicketProgressTransferInitialValues | null>(null)
  const sessionUser = useAtomValue(sessionUserAtom)
  const { canUpdate } = usePermission()
  const canEditTicket = canUpdate("tickets")
  const [detailModalState, setDetailModalState] = useState<{
    open: boolean
    title: string
    value: string
    field: "propertyNote" | "tenantNote" | "ocComment" | "ownerNote" | null
  }>({
    open: false,
    title: "",
    value: "",
    field: null,
  })
  const [editModalState, setEditModalState] = useState<{
    open: boolean
    title: string
    value: string
    field: "propertyNote" | "tenantNote" | "ocComment" | "ownerNote" | null
  }>({
    open: false,
    title: "",
    value: "",
    field: null,
  })
  const [editSupportHistoryModalState, setEditSupportHistoryModalState] = useState<{
    open: boolean
    comment: TicketComment | null
  }>({
    open: false,
    comment: null,
  })
  const [deleteSupportHistoryTarget, setDeleteSupportHistoryTarget] =
    useState<TicketComment | null>(null)

  const { data, refetch } = ticketApi.useTicketGetSuspense(ticketId || "", {
    withOwner: TicketGetWithOwner.true,
    withProperty: TicketGetWithProperty.true,
    withRoom: TicketGetWithRoom.true,
    withRoomPlans: TicketGetWithRoomPlans.true,
    withTenant: TicketGetWithTenant.true,
    withOcAssignee: TicketGetWithOcAssignee.true,
    withTicketComments: TicketGetWithTicketComments.true,
    withTicketCommentsContactFromOwner: TicketGetWithTicketCommentsContactFromOwner.true,
    withTicketCommentsContactFromTenant: TicketGetWithTicketCommentsContactFromTenant.true,
    withTicketCommentsContactFromAdmin: TicketGetWithTicketCommentsContactFromAdmin.true,
    withTicketCommentsContactToOwner: TicketGetWithTicketCommentsContactToOwner.true,
    withTicketCommentsContactToTenant: TicketGetWithTicketCommentsContactToTenant.true,
    withTicketCommentsContactToAdmin: TicketGetWithTicketCommentsContactToAdmin.true,
  })

  // 物件注意事項更新用mutation
  const updatePropertyNote = propertyApi.usePropertyUpdate({
    mutation: {
      onSuccess: () => {
        refetch()
      },
    },
  })

  // OCコメント更新用mutation
  const updateTicketOcComment = ticketApi.useTicketUpdate({
    mutation: {
      onSuccess: () => {
        refetch()
      },
    },
  })

  // 賃借人注意事項更新用mutation
  const updateTenantMemo = tenantApi.useTenantUpdateAdminMemo({
    mutation: {
      onSuccess: () => {
        refetch()
      },
    },
  })

  // オーナー注意事項更新用mutation
  const updateOwnerMemo = ownerApi.useOwnerUpdateAdminMemo({
    mutation: {
      onSuccess: () => {
        refetch()
      },
    },
  })

  const deleteSupportHistoryMutation = ticketApi.useTicketCommentDelete({
    mutation: {
      onSuccess: () => {
        setDeleteSupportHistoryTarget(null)
        refetch()
        toast.success("対応履歴を削除しました")
      },
      onError: () => {
        toast.error("対応履歴の削除に失敗しました")
      },
    },
  })

  if (!data?.ticket) {
    throw new Error("案件が見つかりません")
  }

  const ticket = data.ticket
  const planInfo = roomLib.getRoomServicePlanInfo(ticket.room)

  // navigation stateからrefetchフラグをチェック
  useEffect(() => {
    if (location.state?.shouldRefetch) {
      refetch()
    }
  }, [location.state, refetch])

  const handleAddSupportHistory = () => {
    setProgressTransferInitialValues(null)
    setOpensAddSupportHistoryModal(true)
  }

  const handleTransferMessageToProgress = (message: TicketMessageList200TicketMessagesItem) => {
    if (opensAddSupportHistoryModal) {
      const shouldReplace = window.confirm(
        "入力中の進捗追加を破棄して、選択したチャット本文を反映しますか？"
      )
      if (!shouldReplace) return
    }

    const isAdminMessage = message.senderType === "admin"
    const ownerId = message.ownerId ?? message.owner?.id ?? ticket.owner?.id ?? ""
    const ownerName = message.owner?.name ?? ticket.owner?.name ?? ""
    const adminId = message.adminId ?? ""
    const adminName = message.admin?.name ?? ""

    const initialValues: TicketProgressTransferInitialValues = {
      contactAt: message.createdAt,
      contactType: ticketType.CONTACT_TYPE_CHAT,
      content: message.message,
      ocAssigneeId: sessionUser?.id ?? ticket.ocAssigneeId ?? "",
      ...(isAdminMessage
        ? {
            contactFromCategory: ticketType.CONTACT_CATEGORY_OC,
            contactFromId: adminId,
            contactFromText: adminId ? "" : adminName,
            contactToCategory: ticketType.CONTACT_CATEGORY_OWNER,
            contactToId: ownerId,
            contactToText: ownerId ? "" : ownerName,
          }
        : {
            contactFromCategory: ticketType.CONTACT_CATEGORY_OWNER,
            contactFromId: ownerId,
            contactFromText: ownerId ? "" : ownerName,
            contactToCategory: ticketType.CONTACT_CATEGORY_OC,
            contactToId: sessionUser?.id ?? "",
            contactToText: sessionUser?.id ? "" : sessionUser?.name || sessionUser?.email || "",
          }),
    }

    setProgressTransferInitialValues(initialValues)
    setOpensAddSupportHistoryModal(true)
  }

  const handleEditNote = (
    field: "propertyNote" | "tenantNote" | "ocComment" | "ownerNote",
    title: string,
    value: string
  ) => {
    // すべての項目で詳細モーダルを開く
    setDetailModalState({
      open: true,
      title,
      value: value || "",
      field,
    })
  }

  const handleOpenEditModal = () => {
    setEditModalState({
      open: true,
      title: detailModalState.title,
      value: detailModalState.value,
      field: detailModalState.field,
    })
    setDetailModalState({
      open: false,
      title: "",
      value: "",
      field: null,
    })
  }

  // 失敗したら throw し、モーダルに入力を残させる（表示は共通のトースト）
  const handleSaveNote = async (value: string) => {
    const { field } = editModalState

    if (field === "propertyNote") {
      if (!ticket.property) {
        console.error("物件情報が見つかりません")
        return
      }
      await updatePropertyNote.mutateAsync({
        id: String(ticket.property.id),
        data: {
          ownerId: ticket.property.ownerId,
          name: ticket.property.name,
          nameKana: ticket.property.nameKana,
          upperZip: ticket.property.upperZip,
          lowerZip: ticket.property.lowerZip,
          prefecture: ticket.property.prefecture,
          prefectureKana: ticket.property.prefectureKana,
          city: ticket.property.city,
          cityKana: ticket.property.cityKana,
          town: ticket.property.town,
          townKana: ticket.property.townKana,
          district: ticket.property.district,
          districtKana: ticket.property.districtKana,
          builtYear: ticket.property.builtYear,
          structure: ticket.property.structure,
          type: ticket.property.type,
          floor: ticket.property.floor,
          managementCompanyName: ticket.property.managementCompanyName,
          managementCompanyPhone: ticket.property.managementCompanyPhone,
          note: value,
        },
      })
    } else if (field === "ocComment") {
      await updateTicketOcComment.mutateAsync({
        id: ticketId || "",
        data: {
          status: ticket.status || "oc_in_progress",
          title: ticket.title || "",
          category: ticket.category || "toiawase",
          ocAssigneeId: ticket.ocAssigneeId || null,
          occurredAt: ticket.occurredAt,
          contractorStatus: ticket.contractorStatus || null,
          contractorType: ticket.contractorType || null,
          contractorName: ticket.contractorName || "",
          contractorContact: ticket.contractorContact || "",
          contractorContactInfo: ticket.contractorContactInfo || "",
          contractorScheduledDate: ticket.contractorScheduledDate || null,
          ocComment: value,
          alertAt: ticket.alertAt || null,
          alertComment: ticket.alertComment || "",
          note: ticket.note || "",
        },
      })
    } else if (field === "tenantNote") {
      if (!ticket.tenantId) {
        console.error("賃借人IDが存在しません")
        return
      }
      await updateTenantMemo.mutateAsync({
        id: String(ticket.tenantId),
        data: { adminMemo: value },
      })
    } else if (field === "ownerNote") {
      if (!ticket.owner?.id) {
        console.error("オーナーIDが存在しません")
        return
      }
      await updateOwnerMemo.mutateAsync({
        id: String(ticket.owner.id),
        data: { adminMemo: value },
      })
    }

    setEditModalState({
      open: false,
      title: "",
      value: "",
      field: null,
    })
  }

  const handleProgressAddSuccess = () => {
    setProgressTransferInitialValues(null)
    refetch()
  }

  const handleEditSupportHistory = (comment: TicketComment) => {
    setEditSupportHistoryModalState({
      open: true,
      comment,
    })
  }

  const handleDeleteSupportHistory = () => {
    if (!deleteSupportHistoryTarget) return

    deleteSupportHistoryMutation.mutate({
      id: deleteSupportHistoryTarget.ticketId,
      ticketCommentId: deleteSupportHistoryTarget.id,
    })
  }

  return (
    <>
      <div className={cn("flex-1 overflow-auto hidden", activeTab === "progress" && "block")}>
        <div className="mt-4 grid grid-cols-[minmax(315px,400px)_minmax(315px,400px)_minmax(480px,_auto)] gap-4">
          {/* Left Column - Basic Info */}
          <div className="space-y-4">
            {/* Basic Information Card */}
            <Card>
              <div className="text-xs text-gray-500 p-4 border-b-slate-200 border-b">
                <div className="flex gap-1">
                  <span className="text-slate-400">作成日時</span>
                  {ticket.createdAt && format(new Date(ticket.createdAt), "yyyy-MM-dd HH:mm")}
                </div>
                <div className="flex gap-1">
                  <span className="text-slate-400">更新日時</span>
                  {ticket.updatedAt && format(new Date(ticket.updatedAt), "yyyy-MM-dd HH:mm")}
                </div>
              </div>
              <div className="space-y-4 p-4 border-b-slate-200 border-b">
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <div className="text-xs text-slate-400 mb-1">ステータス</div>
                    <Label variant={getStatusVariant(ticket.status)}>
                      {getStatusLabel(ticket.status)}
                    </Label>
                  </div>
                  <div>
                    <div className="text-xs text-slate-400 mb-1">担当者</div>
                    <div className="text-sm">{ticket.ocAssignee?.name || "-"}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-400 mb-1">カテゴリ</div>
                    <div className="text-sm">
                      {getCategoryLabel(ticket.category, ticket.subCategory)}
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <div className="text-xs text-slate-400 mb-1">物件</div>
                      <div className="text-sm">
                        {ticket.property?.id ? (
                          <span
                            className="text-[#458CF7] cursor-pointer hover:opacity-80"
                            onClick={() => openInNewTab(`/properties/${ticket.property?.id}`)}
                          >
                            {ticket.property?.name || "-"}
                          </span>
                        ) : (
                          ticket.property?.name || "-"
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-400 mb-1">部屋番号</div>
                      <div className="text-sm">
                        {ticket.room?.id && ticket.property?.id ? (
                          <span
                            className="text-[#458CF7] cursor-pointer hover:opacity-80"
                            onClick={() =>
                              openInNewTab(
                                `/properties/${ticket.property?.id}/rooms/${ticket.room?.id}`
                              )
                            }
                          >
                            {ticket.room?.name || "-"}
                          </span>
                        ) : (
                          ticket.room?.name || "-"
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-400 mb-1">賃借人</div>
                      <div className="text-sm">
                        {ticket.tenant?.id ? (
                          <span
                            className="text-[#458CF7] cursor-pointer hover:opacity-80"
                            onClick={() => openInNewTab(`/tenants/${ticket.tenant?.id}`)}
                          >
                            {ticket.tenant?.name || "-"}
                          </span>
                        ) : (
                          ticket.tenant?.name || "-"
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-xs text-slate-400 mb-1">賃借人連絡先</div>
                    <div className="text-sm">
                      {ticket.tenant
                        ? formatPhone(
                            ticket.tenant.phone1,
                            ticket.tenant.phone2,
                            ticket.tenant.phone3
                          )
                        : "-"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-400 mb-1">加入プラン</div>
                    <Label variant={planInfo.variant}>{planInfo.label}</Label>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-slate-400 mb-1">アラート</div>
                      <div className="text-sm">
                        {ticket.alertAt
                          ? format(new Date(ticket.alertAt), "yyyy-MM-dd HH:mm")
                          : "-"}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-slate-400 mb-1">アラートコメント</div>
                      <div className="text-sm">{ticket.alertComment || "-"}</div>
                    </div>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-slate-400 mb-1">業者依頼状況</div>
                      {ticket.contractorStatus ? (
                        <Label variant={getContractorStatusVariant(ticket.contractorStatus)}>
                          {getContractorStatusLabel(ticket.contractorStatus)}
                        </Label>
                      ) : (
                        "-"
                      )}
                    </div>
                    <div>
                      <div className="text-xs text-slate-400 mb-1">手配先業者</div>
                      <div className="text-sm">{getContractorTypeLabel(ticket.contractorType)}</div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Notes Cards */}
            <NoteSection
              title="物件注意事項"
              content={ticket.property?.note || undefined}
              showEditIcon={canEditTicket}
              onEditClick={() =>
                handleEditNote("propertyNote", "物件注意事項", ticket.property?.note || "")
              }
            />

            <NoteSection
              title="賃借人注意事項"
              content={ticket.tenant?.adminMemo || undefined}
              defaultContent={ticket.tenantId ? "注意事項はありません" : "賃借人が存在しません"}
              showEditIcon={canEditTicket && !!ticket.tenantId}
              onEditClick={() =>
                handleEditNote("tenantNote", "賃借人注意事項", ticket.tenant?.adminMemo || "")
              }
            />

            <NoteSection
              title="OCコメント"
              content={ticket.ocComment || undefined}
              defaultContent="コメントはありません"
              showEditIcon={canEditTicket}
              onEditClick={() => handleEditNote("ocComment", "OCコメント", ticket.ocComment || "")}
            />

            <NoteSection
              title="オーナー注意事項"
              content={ticket.owner?.adminMemo || undefined}
              defaultContent={ticket.owner?.id ? "注意事項はありません" : "オーナーが存在しません"}
              showEditIcon={canEditTicket && !!ticket.owner?.id}
              onEditClick={() =>
                handleEditNote("ownerNote", "オーナー注意事項", ticket.owner?.adminMemo || "")
              }
            />
          </div>

          {/* Middle Column - Owner Info & Chat */}
          <div className="grid h-0 min-h-full grid-rows-[auto_minmax(0,1fr)] gap-4 overflow-hidden">
            {/* Owner Information Card */}
            <PropertyOwnerInfo owner={ticket.owner} propertyId={ticket.property?.id} />

            {/* Chat Card */}
            <ReactErrorBoundary>
              <Suspense fallback={<SectionLoading />}>
                <ContactInfo
                  ticketId={ticketId || ""}
                  ownerName={ticket.owner?.name || "山田太郎"}
                  isMessageReadByAdmin={ticket.isMessageReadByAdmin}
                  propertyId={ticket.propertyId}
                  roomId={ticket.roomId}
                  onTransferMessageToProgress={handleTransferMessageToProgress}
                />
              </Suspense>
            </ReactErrorBoundary>
          </div>

          {/* Right Column - Response History */}
          <div className="h-0 min-h-full overflow-hidden">
            <SupportHistory
              historyTab={historyTab}
              onHistoryTabChange={setHistoryTab}
              onAddSupportHistory={handleAddSupportHistory}
              onEditSupportHistory={handleEditSupportHistory}
              onDeleteSupportHistory={canEditTicket ? setDeleteSupportHistoryTarget : undefined}
              ticketComments={ticket.ticketComments ?? undefined}
              showAddButton={canEditTicket}
            />
          </div>
        </div>
      </div>

      <div className={cn("hidden", activeTab === "info" && "block")}>
        <TicketInfoTab ticket={ticket} />
      </div>

      <TicketProgressAddModal
        open={opensAddSupportHistoryModal}
        onOpenChange={(open) => {
          setOpensAddSupportHistoryModal(open)
          if (!open) {
            setProgressTransferInitialValues(null)
          }
        }}
        ticketId={ticketId || ""}
        ticket={ticket}
        onSuccess={handleProgressAddSuccess}
        transferInitialValues={progressTransferInitialValues}
      />

      <NoteDetailModal
        open={detailModalState.open}
        onOpenChange={(open) => setDetailModalState((prev) => ({ ...prev, open }))}
        title={detailModalState.title}
        content={detailModalState.value}
        onEdit={handleOpenEditModal}
      />

      <NoteEditModal
        open={editModalState.open}
        onOpenChange={(open) => setEditModalState((prev) => ({ ...prev, open }))}
        title={editModalState.title}
        initialValue={editModalState.value}
        onSave={handleSaveNote}
      />

      <SupportHistoryEditModal
        open={editSupportHistoryModalState.open}
        onOpenChange={(open) => setEditSupportHistoryModalState((prev) => ({ ...prev, open }))}
        comment={editSupportHistoryModalState.comment || undefined}
        onSave={() => refetch()}
      />

      <Dialog
        open={deleteSupportHistoryTarget !== null}
        onOpenChange={(open) => {
          if (!open && !deleteSupportHistoryMutation.isPending) {
            setDeleteSupportHistoryTarget(null)
          }
        }}
      >
        <DialogContent className="max-w-[463px] rounded-[15px] p-[30px]">
          <DialogHeader className="gap-5">
            <DialogTitle className="flex items-center gap-2.5 text-[22px] font-semibold tracking-[-0.165px] text-slate-600">
              <AlertTriangle className="text-red-500" size={24} />
              対応履歴を削除しますか？
            </DialogTitle>
            <DialogDescription className="text-sm font-medium tracking-[-0.105px] text-slate-500">
              削除した対応履歴は元に戻せません。案件の現在の情報は変更されません。
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-2 gap-3">
            <Button
              variant="outline"
              onClick={() => setDeleteSupportHistoryTarget(null)}
              disabled={deleteSupportHistoryMutation.isPending}
            >
              キャンセル
            </Button>
            <Button
              className="bg-red-500 text-white hover:bg-red-600"
              onClick={handleDeleteSupportHistory}
              loading={deleteSupportHistoryMutation.isPending}
            >
              削除する
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

// Helper functions
function getStatusVariant(
  status?: TicketModels.TicketGet200TicketStatus
): "oc" | "vendor" | "owner" | "completed" | "default" {
  switch (status) {
    case "oc_in_progress":
      return "oc"
    case "owner_in_progress":
      return "owner"
    case "contractor_in_progress":
      return "vendor"
    case "completed":
      return "completed"
    default:
      return "default"
  }
}

function getStatusLabel(status?: TicketModels.TicketGet200TicketStatus): string {
  switch (status) {
    case "oc_in_progress":
      return "OC対応中"
    case "owner_in_progress":
      return "オーナー対応中"
    case "contractor_in_progress":
      return "業者対応中"
    case "completed":
      return "完了"
    default:
      return "-"
  }
}

function getContractorStatusVariant(
  status?: TicketModels.TicketGet200TicketContractorStatus
): "approved" | "unapproved" | "default" | "completed" {
  switch (status) {
    case "estimate_requested":
      return "default"
    case "work_requested":
      return "default"
    case "order_requested":
      return "default"
    case "estimate_received":
      return "unapproved"
    case "owner_confirming":
      return "unapproved"
    case "completed":
      return "completed"
    default:
      return "default"
  }
}

function getContractorStatusLabel(
  status?: TicketModels.TicketGet200TicketContractorStatus
): string {
  switch (status) {
    case "estimate_requested":
      return "見積依頼中"
    case "work_requested":
      return "作業依頼中"
    case "order_requested":
      return "発注依頼中"
    case "estimate_received":
      return "見積受領済"
    case "owner_confirming":
      return "オーナー確認中"
    case "completed":
      return "完了"
    default:
      return "-"
  }
}

function getContractorTypeLabel(type?: TicketModels.TicketGet200TicketContractorType): string {
  switch (type) {
    case "owner_specified":
      return "オーナー指定業者"
    case "agentcall":
      return "AC指定業者"
    default:
      return "-"
  }
}

function getCategoryLabel(
  category?: TicketModels.TicketGet200TicketCategory,
  subCategory?: TicketModels.TicketGet200TicketSubCategory
): string {
  return `${ticketLib.getCategoryLabel(category || "")} / ${ticketLib.getSubCategoryLabel(subCategory || "")}`
}
